import { afterNextRender, ChangeDetectionStrategy, Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { DateTime } from 'luxon';
import { ICouponUse, IPackage } from '../../../types';
import { ProfileService } from '../profile.service';
import { MatIcon } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatError, MatHint } from '@angular/material/form-field';
import { PaintDirective } from '../../../paint.directive';
import { DatePipe } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
  selector: 'profile-coupons',
  templateUrl: './coupons.component.html',
  styleUrls: ['./coupons.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('dissolve', [
      state('true', style({
        height: '*',
        opacity: 1,
        display: 'block'
      })),
      state('false', style({
        height: '0px',
        opacity: 0,
        display: 'none'
      })),
      transition('false => true', [
        style({ display: 'block' }),
        animate(400, style({
          // transform: 'translateY(0%)',
          height: '*',
          opacity: 1
        }))
      ]),
      transition('true => false', [
        animate(400, style({
          // transform: 'translateY(-150%)',
          height: '0px',
          opacity: 0
        })),
      ])
    ])
  ],
  standalone: true,
  imports: [MatIcon, MatButtonModule, MatHint, MatError, ReactiveFormsModule, PaintDirective, DatePipe]
})
export class ProfileCouponsComponent {
  service = inject(ProfileService);
  destroy = inject(DestroyRef);

  showUnused = signal<boolean>(false);
  showUsed = signal<boolean>(false);
  showExpired = signal<boolean>(false);
  couponEntry: FormGroup = new FormGroup({
    code: new FormControl('', { validators: [Validators.required], nonNullable: true })
  });
  error = signal<string|null>(null);
  
  coupon_uses = signal<ICouponUse[]>([]);
  packages = signal<{ [key: string]: IPackage }>({});

  expired = computed(() => this.coupon_uses().filter(use => use.expiry && DateTime.fromISO(use.expiry) <= DateTime.now()));
  used = computed(() => this.coupon_uses().filter(use => use.used && !this.expired().includes(use)));
  unused = computed(() => this.coupon_uses().filter(use => !use.used && !this.expired().includes(use)));

  constructor() { 
    afterNextRender(() => {
      this.getCoupons();
    });
  }
  
  getCoupons(){
    this.service.loading.set(true);
    this.service.getCoupons().pipe(takeUntilDestroyed(this.destroy)).subscribe({
      next: ({ uses: coupon_uses, packages }) => {
        this.packages.set(packages.reduce<{ [key: string]: IPackage }>((t,c) => {
          t[c._id] = c
          return t;
        }, {}));
        
        this.coupon_uses.set(coupon_uses);
        this.service.loading.set(false);

      },
      error: error => {
        this.service.toast.error('Could not load discount codes. Please try again later')
        this.service.loading.set(false);
      }
    });
  }

  submit(callback?: Function){
    if(this.couponEntry.valid){
      this.service.loading.set(true);
      this.service.addCoupon(this.couponEntry.get('code')!.value).subscribe(
        use => {
          this.service.loading.set(false);
          
          const pkg = use.coupon.package as any as IPackage;

          if(pkg && !this.packages()[pkg._id]){
            this.packages.update(packages => ({ ...packages, [pkg._id]: pkg }))
          }

          this.coupon_uses.update(uses => ([...uses, use]));

          this.couponEntry.get('code')!.setValue('');
          this.error.set(null);
          this.getCoupons();

          if(use.coupon.bypass && this.service.auth.isFree() && (!pkg || pkg.package_type == 'corporate')){
            this.service.useCorporate(use._id, this.service.router.isFromApp() ? 'app' : 'web').subscribe({
              next: validation => {
                if(!validation)
                  throw new Error('#Request failed');
        
                this.service.auth.sync();
                this.service.toast.success('Please download the cloudigo app to use your discounts!', 'Membership Activated');
              
                this.getCoupons();
                if(callback){
                  callback();
                }
              },
              error: error => {
                //woops
              this.service.loading.set(false);
              }
            });
          }
        },
        error => {
          error = error.error||error;
          this.error.set(error.message);

          this.service.loading.set(false);
        }
      );
    }
  }

}
